import React, { useEffect, useState, useRef } from 'react';
import {
  AppBar,
  Tabs,
  Tab,
  Box,
  TextField,
  Button,
  MenuItem,
  Typography,
  Paper,
  CircularProgress,
  Skeleton,
  Select,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  Slide
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getBrandNotificationIcon } from '../branding/BrandAsset';
import axios from 'axios';
import {
  ChartCanvas,
  Chart,
  CandlestickSeries,
  XAxis,
  YAxis,
  CrossHairCursor,
  EdgeIndicator,
  CurrentCoordinate,
  MouseCoordinateX,
  MouseCoordinateY,
} from 'react-financial-charts';
import { format } from 'd3-format';
import { timeFormat } from 'd3-time-format';
import { discontinuousTimeScaleProvider } from "@react-financial-charts/scales";

// TradingView-like color scheme
const tradingViewColors = {
  green: '#26a69a',
  red: '#ef5350',
  text: '#e0e0e0',
  background: '#121826',
  grid: '#2a2e39',
  crosshair: '#758696',
};

// Constants
const SYMBOL_CATEGORIES = {
  Volatility: ['R_10', 'R_25', 'R_50', 'R_75', 'R_100', 'RDBULL', 'RDBEAR', '1HZ10V', '1HZ25V', '1HZ50V', '1HZ75V', '1HZ100V'],
  Forex: ['frxAUDCAD', 'frxAUDCHF', 'frxAUDJPY', 'frxAUDNZD', 'frxAUDUSD', 'frxEURAUD', 'frxEURCAD', 'frxEURCHF', 'frxEURGBP', 'frxEURJPY', 'frxEURUSD', 'frxGBPAUD', 'frxGBPCAD', 'frxGBPCHF', 'frxGBPJPY', 'frxGBPUSD', 'frxNZDJPY', 'frxNZDUSD', 'frxUSDCAD', 'frxUSDCHF', 'frxUSDJPY'],
  BoomCrash: ['Boom 1000 Index', 'Crash 1000 Index', 'Boom 500 Index', 'Crash 500 Index'],
  Metals: ['XAUUSD', 'XAGUSD'],
  Crypto: ['BTCUSD', 'ETHUSD', 'LTCUSD', 'USDCUSD', 'eUSDTUSD', 'tUSDTUSD']
};

const TIMEFRAME_OPTIONS = [
  { value: 60, label: '1 Minute' },
  { value: 120, label: '2 Minutes' },
  { value: 180, label: '3 Minutes' },
  { value: 300, label: '5 Minutes' },
  { value: 900, label: '15 Minutes' },
  { value: 1800, label: '30 Minutes' },
  { value: 3600, label: '1 Hour' },
  { value: 14400, label: '4 Hours' },
  { value: 86400, label: '1 Day' },
  { value: 604800, label: '1 Week' },
];

const PRICE_STATE_TEMPLATE = {
  value: null,
  loading: false,
  error: null,
  lastUpdated: null
};

// TradingView-style price formatter
const priceFormatter = format('.2~f');
const dateFormatter = timeFormat('%H:%M:%S');

// API Configuration
const API_BASE_URL = 'https://protradesinsight.org/backend/public/api/';

// Configure axios defaults
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
});

// Alert Popup Component
const AlertPopup = ({ open, onClose, severity, message }) => {
  return (
    <Snackbar
      open={open}
      autoHideDuration={6000}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      TransitionComponent={Slide}
      sx={{
        '& .MuiPaper-root': {
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }
      }}
    >
      <Alert
        onClose={onClose}
        severity={severity}
        variant="filled"
        sx={{
          width: '100%',
          alignItems: 'center',
          '& .MuiAlert-icon': {
            fontSize: '1.5rem',
            marginRight: '12px'
          },
          '& .MuiAlert-message': {
            fontSize: '0.875rem',
            fontWeight: 500
          }
        }}
      >
        {message}
      </Alert>
    </Snackbar>
  );
};

// Enhanced CandleChart Component
const CandleChart = ({ data, loading, width, currentPrice }) => {
  const chartContainerRef = useRef(null);
  const [initialScrollDone, setInitialScrollDone] = useState(false);

  useEffect(() => {
    if (!loading && data.length > 0 && chartContainerRef.current && !initialScrollDone) {
      const scrollContainer = chartContainerRef.current;
      scrollContainer.scrollLeft = scrollContainer.scrollWidth;
      setInitialScrollDone(true);
    }
  }, [loading, data, initialScrollDone]);

  if (loading) {
    return (
      <Box sx={{ 
        height: 400, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexDirection: 'column',
        backgroundColor: tradingViewColors.background,
      }}>
        <CircularProgress />
        <Typography sx={{ mt: 2, color: tradingViewColors.text }}>
          Loading market data...
        </Typography>
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={{ 
        height: 400, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexDirection: 'column',
        backgroundColor: tradingViewColors.background,
      }}>
        <Typography sx={{ color: tradingViewColors.text }}>
          No chart data available
        </Typography>
        <Typography variant="caption" sx={{ mt: 1, color: tradingViewColors.text }}>
          The selected symbol may not have historical data
        </Typography>
      </Box>
    );
  }

  try {
    const xScaleProvider = discontinuousTimeScaleProvider.inputDateAccessor(d => new Date(d.date));
    const { data: chartData, xScale, xAccessor, displayXAccessor } = xScaleProvider(data);
    const start = xAccessor(chartData[0]);
    const end = xAccessor(chartData[chartData.length - 1]);

    const candleWidth = 6;
    const chartWidth = Math.max(width, chartData.length * candleWidth);

    return (
      <Box 
        ref={chartContainerRef}
        sx={{ 
          width: '100%', 
          overflowX: 'auto',
          backgroundColor: tradingViewColors.background,
          borderRadius: '4px',
          '&::-webkit-scrollbar': {
            height: '8px',
            backgroundColor: tradingViewColors.grid,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: tradingViewColors.green,
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: '#1d8a7e',
            },
          },
        }}
      >
        <Box sx={{ 
          minWidth: chartWidth,
          position: 'relative',
          height: 400,
        }}>
          <ChartCanvas
            height={400}
            width={chartWidth}
            ratio={1}
            margin={{ left: 50, right: 50, top: 10, bottom: 30 }}
            data={chartData}
            xScale={xScale}
            xAccessor={xAccessor}
            displayXAccessor={displayXAccessor}
            seriesName="Candles"
            xExtents={[start, end]}
            padding={{ left: 20, right: 20 }}
            panEvent={true}
            zoomEvent={true}
          >
            <Chart 
              id={1} 
              yExtents={d => [d.high, d.low]}
              padding={{ top: 10, bottom: 20 }}
            >
              <XAxis 
                stroke={tradingViewColors.grid}
                tickStroke={tradingViewColors.grid}
                tickLabelFill={tradingViewColors.text}
              />
              <YAxis 
                stroke={tradingViewColors.grid}
                tickStroke={tradingViewColors.grid}
                tickLabelFill={tradingViewColors.text}
                ticks={10}
              />
              
              <CandlestickSeries
                wickStroke={d => d.close > d.open ? tradingViewColors.green : tradingViewColors.red}
                fill={d => d.close > d.open ? tradingViewColors.green : tradingViewColors.red}
                stroke={d => d.close > d.open ? tradingViewColors.green : tradingViewColors.red}
              />
              
              {currentPrice && (
                <CurrentCoordinate
                  yAccessor={() => currentPrice}
                  fill={tradingViewColors.text}
                  stroke={tradingViewColors.crosshair}
                  strokeDasharray="ShortDash"
                />
              )}
              
              <MouseCoordinateX
                displayFormat={dateFormatter}
                fill={tradingViewColors.background}
                textFill={tradingViewColors.text}
              />
              <MouseCoordinateY
                displayFormat={priceFormatter}
                fill={tradingViewColors.background}
                textFill={tradingViewColors.text}
              />
              
              <EdgeIndicator
                itemType="last"
                orient="right"
                displayFormat={priceFormatter}
                fill={d => d.close > d.open ? tradingViewColors.green : tradingViewColors.red}
                lineStroke={d => d.close > d.open ? tradingViewColors.green : tradingViewColors.red}
                textFill={tradingViewColors.text}
                yAccessor={d => d.close}
              />
            </Chart>
            <CrossHairCursor 
              stroke={tradingViewColors.crosshair}
              strokeDasharray="ShortDash"
            />
          </ChartCanvas>
        </Box>
      </Box>
    );
  } catch (error) {
    console.error('Chart rendering error:', error);
    return (
      <Box sx={{ 
        height: 400, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: tradingViewColors.background,
      }}>
        <Typography sx={{ color: tradingViewColors.text }}>
          Error rendering chart
        </Typography>
      </Box>
    );
  }
};

// PriceDisplay Component
const PriceDisplay = ({ priceState, onRefresh }) => {
  if (priceState.loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Skeleton width={100} height={24} />
        <Skeleton width={80} height={16} />
      </Box>
    );
  }

  if (priceState.error) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography color="error">{priceState.error}</Typography>
        <Button 
          size="small" 
          onClick={onRefresh}
          startIcon={<RefreshIcon />}
          sx={{ color: tradingViewColors.text }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  if (priceState.value !== null) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ color: tradingViewColors.text }}>
          Live Price: {priceState.value.toFixed(5)}
        </Typography>
        {priceState.lastUpdated && (
          <Typography variant="caption" sx={{ color: tradingViewColors.text }}>
            (Updated: {new Date(priceState.lastUpdated).toLocaleTimeString()})
          </Typography>
        )}
      </Box>
    );
  }

  return <Typography sx={{ color: tradingViewColors.text }}>Establishing connection...</Typography>;
};

// Main Component
export default function PriceAlertRealtime() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [width, setWidth] = useState(600);
  const [category, setCategory] = useState('Volatility');
  const [symbol, setSymbol] = useState('R_10');
  const [price, setPrice] = useState('');
  const [alerts, setAlerts] = useState([]);
  const [ohlcData, setOhlcData] = useState([]);
  const [processedAlerts, setProcessedAlerts] = useState(new Set());
  const [monitoredSymbols, setMonitoredSymbols] = useState(new Set());
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [timeframe, setTimeframe] = useState(3600);
  const [chartLoading, setChartLoading] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState('success');
  const [alertMessage, setAlertMessage] = useState('');
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [userChatId, setUserChatId] = useState(null);
  const [apiStatus, setApiStatus] = useState('checking');
  
  const livePricesRef = useRef({});
  const prevSymbols = useRef(new Set());
  const candleWsRef = useRef(null);
  const priceWsRef = useRef(null);
  const currentCandleRef = useRef(null);
  const lastCandleTimeRef = useRef(null);
  const initialLoadDone = useRef(false);

  const [symbolLivePrices, setSymbolLivePrices] = useState(
    Object.fromEntries(
      Object.values(SYMBOL_CATEGORIES)
        .flat()
        .map(sym => [sym, { ...PRICE_STATE_TEMPLATE }])
    )
  );

  // Check API health on component mount
  useEffect(() => {
    const checkApiHealth = async () => {
      try {
        const response = await api.get('/health');
        if (response.data.status === 'ok') {
          setApiStatus('connected');
        } else {
          setApiStatus('error');
        }
      } catch (error) {
        console.error('API health check failed:', error);
        setApiStatus('error');
      }
    };

    checkApiHealth();
  }, []);

  // Initialize user chat ID from localStorage or prompt
  useEffect(() => {
    const savedChatId = localStorage.getItem('telegram_chat_id');
    if (savedChatId) {
      setUserChatId(savedChatId);
    } else {
      // For demo purposes, prompt for chat ID
      const chatId = prompt('Please enter your Telegram Chat ID (you can get this by messaging your bot and checking the webhook logs):');
      if (chatId) {
        setUserChatId(chatId);
        localStorage.setItem('telegram_chat_id', chatId);
      }
    }
  }, []);

  // Initialize push notifications
  useEffect(() => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      navigator.serviceWorker.ready.then(registration => {
        console.log('Service Worker is ready');
      });

      const registerServiceWorker = async () => {
        try {
          const registration = await navigator.serviceWorker.register('/service-worker.js');
          console.log('Service Worker registered');

          const permission = await Notification.requestPermission();
          setNotificationPermission(permission);
          console.log('Notification permission:', permission);
        } catch (error) {
          console.error('Service Worker registration failed:', error);
        }
      };

      registerServiceWorker();
    }
  }, []);

  // Show browser notification
  const showBrowserNotification = (title, options) => {
    if (notificationPermission !== 'granted') return;

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(registration => {
        registration.showNotification(title, {
          body: options.body || '',
          icon: getBrandNotificationIcon(),
          vibrate: [200, 100, 200, 100, 200], // Vibration pattern for mobile
          data: {
            url: window.location.href,
            symbol: options.symbol || ''
          },
          ...options
        });
      });
    } else {
      // Fallback for browsers without service worker support
      new Notification(title, {
        body: options.body,
        icon: getBrandNotificationIcon()
      });
    }
  };

  // Enhanced showAlert function that also triggers push notifications
  const showAlert = (severity, message, notificationOptions = {}) => {
    setAlertSeverity(severity);
    setAlertMessage(message);
    setAlertOpen(true);

    // Only send push notifications for important alerts (warnings/errors)
    if (severity === 'warning' || severity === 'error') {
      showBrowserNotification('Price Alert', {
        body: message,
        symbol: symbol,
        ...notificationOptions
      });
    }
  };

  // Calculate responsive width
  useEffect(() => {
    const updateWidth = () => {
      const containerWidth = document.getElementById('chart-container')?.offsetWidth || 600;
      setWidth(Math.min(containerWidth, 800));
    };
    
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Initialize with cached prices
  useEffect(() => {
    const cachedPrices = JSON.parse(localStorage.getItem('priceCache')) || {};
    setSymbolLivePrices(prev => {
      const updates = {};
      Object.entries(cachedPrices).forEach(([sym, data]) => {
        if (Date.now() - data.timestamp < 60000) {
          updates[sym] = {
            value: data.value,
            loading: false,
            error: null,
            lastUpdated: new Date(data.timestamp)
          };
        }
      });
      return { ...prev, ...updates };
    });
  }, []);

  const updatePriceCache = (symbol, price) => {
    const cache = JSON.parse(localStorage.getItem('priceCache') || '{}');
    cache[symbol] = { value: price, timestamp: Date.now() };
    localStorage.setItem('priceCache', JSON.stringify(cache));
  };

  // UPDATED: Fetch prices from Laravel API with better error handling
  const fetchRealFallbackPrices = async (symbols) => {
    try {
      const response = await api.post('/alerts/prices', {
        symbols: Array.from(symbols)
      });
      
      setSymbolLivePrices(prev => {
        const updates = {};
        Array.from(symbols).forEach(sym => {
          const priceData = response.data[sym];
          updates[sym] = priceData ? {
            value: priceData.price,
            loading: false,
            error: null,
            lastUpdated: new Date(priceData.timestamp)
          } : {
            ...prev[sym],
            loading: false,
            error: 'No price data'
          };
          if (priceData) updatePriceCache(sym, priceData.price);
        });
        return { ...prev, ...updates };
      });
    } catch (error) {
      console.error('Fallback API error:', error);
      setSymbolLivePrices(prev => {
        const updates = {};
        Array.from(symbols).forEach(sym => {
          updates[sym] = {
            ...prev[sym],
            loading: false,
            error: 'Failed to fetch price'
          };
        });
        return { ...prev, ...updates };
      });
    }
  };

  // WebSocket for live prices
  useEffect(() => {
    const currentSymbols = new Set(monitoredSymbols);
    currentSymbols.add(symbol);

    setSymbolLivePrices(prev => {
      const updates = {};
      Array.from(currentSymbols).forEach(sym => {
        updates[sym] = { 
          ...prev[sym], 
          loading: true,
          error: null
        };
      });
      return { ...prev, ...updates };
    });

    const socket = new WebSocket('wss://ws.binaryws.com/websockets/v3?app_id=1089');
    priceWsRef.current = socket;
    const connectionTimeout = setTimeout(() => {
      if (socket.readyState !== WebSocket.OPEN) {
        handleConnectionError('Connection timeout');
      }
    }, 3000);

    const handleConnectionError = (error) => {
      console.error('WebSocket error:', error);
      setConnectionStatus('disconnected');
      fetchRealFallbackPrices(currentSymbols);
    };

    socket.onopen = () => {
      clearTimeout(connectionTimeout);
      setConnectionStatus('connected');
      
      const toSubscribe = [...currentSymbols].filter(s => !prevSymbols.current.has(s));
      toSubscribe.forEach(sym => {
        socket.send(JSON.stringify({ ticks: sym, subscribe: 1 }));
      });
    };

    socket.onmessage = (msg) => {
      const data = JSON.parse(msg.data);
      if (data.tick?.quote) {
        const symbol = data.tick.symbol;
        const price = data.tick.quote;
        updatePriceCache(symbol, price);
        setSymbolLivePrices(prev => ({
          ...prev,
          [symbol]: {
            value: price,
            loading: false,
            error: null,
            lastUpdated: new Date()
          }
        }));
      }
    };

    socket.onerror = (err) => handleConnectionError(err);
    socket.onclose = () => {
      setConnectionStatus('disconnected');
      prevSymbols.current = new Set();
    };

    prevSymbols.current = currentSymbols;
    return () => {
      clearTimeout(connectionTimeout);
      socket.close();
    };
  }, [monitoredSymbols, symbol]);

  // WebSocket for candles with proper loading state management
  useEffect(() => {
    if (!initialLoadDone.current) {
      setChartLoading(true);
    }
    
    currentCandleRef.current = null;
    lastCandleTimeRef.current = null;
    
    if (candleWsRef.current) {
      candleWsRef.current.close();
    }

    const ws = new WebSocket("wss://ws.binaryws.com/websockets/v3?app_id=1089");
    candleWsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({
        ticks_history: symbol,
        style: "candles",
        granularity: timeframe,
        count: 1000,
        subscribe: 1,
        end: "latest"
      }));
    };

    ws.onmessage = (msg) => {
      try {
        const response = JSON.parse(msg.data);
        const now = Date.now();
        const timeframeMs = timeframe * 1000;
        
        if (response.msg_type === 'candles' && response.candles) {
          const formattedData = response.candles.map(candle => ({
            date: candle.epoch * 1000,
            open: parseFloat(candle.open),
            high: parseFloat(candle.high),
            low: parseFloat(candle.low),
            close: parseFloat(candle.close),
          }));
          
          if (formattedData.length > 0) {
            lastCandleTimeRef.current = formattedData[formattedData.length - 1].date;
            
            const nextCandleTime = lastCandleTimeRef.current + timeframeMs;
            
            if (now >= nextCandleTime) {
              currentCandleRef.current = {
                date: nextCandleTime,
                open: symbolLivePrices[symbol]?.value || formattedData[formattedData.length - 1].close,
                high: symbolLivePrices[symbol]?.value || formattedData[formattedData.length - 1].close,
                low: symbolLivePrices[symbol]?.value || formattedData[formattedData.length - 1].close,
                close: symbolLivePrices[symbol]?.value || formattedData[formattedData.length - 1].close,
                isForming: true
              };
              lastCandleTimeRef.current = nextCandleTime;
            }
          }
          
          setOhlcData(formattedData);
          setChartLoading(false);
          initialLoadDone.current = true;
        }
        else if (response.msg_type === 'tick' && response.tick && response.tick.symbol === symbol) {
          const price = parseFloat(response.tick.quote);
          
          if (!lastCandleTimeRef.current || now >= lastCandleTimeRef.current + timeframeMs) {
            if (currentCandleRef.current) {
              setOhlcData(prev => [...prev.slice(-999), {
                ...currentCandleRef.current,
                high: currentCandleRef.current.high,
                low: currentCandleRef.current.low,
                close: currentCandleRef.current.close,
                isForming: false
              }]);
            }
            
            const candleTime = Math.floor(now / timeframeMs) * timeframeMs;
            currentCandleRef.current = {
              date: candleTime,
              open: price,
              high: price,
              low: price,
              close: price,
              isForming: true
            };
            lastCandleTimeRef.current = candleTime;
          }
          else if (currentCandleRef.current) {
            currentCandleRef.current = {
              ...currentCandleRef.current,
              high: Math.max(currentCandleRef.current.high, price),
              low: Math.min(currentCandleRef.current.low, price),
              close: price
            };
          }
          
          setOhlcData(prev => {
            const historicalCandles = currentCandleRef.current?.isForming 
              ? prev.slice(-999) 
              : prev.slice(-1000);
              
            return currentCandleRef.current 
              ? [...historicalCandles, currentCandleRef.current]
              : historicalCandles;
          });
        }
      } catch (error) {
        console.error('Failed to process candle data:', error);
        setChartLoading(false);
      }
    };

    ws.onerror = (error) => {
      console.error('Candle WebSocket error:', error);
      setChartLoading(false);
    };

    ws.onclose = () => {
      console.log('Candle WebSocket closed');
    };

    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, [symbol, timeframe, symbolLivePrices]);

  // UPDATED: Create alert using Laravel API with better error handling
  const handleSubmit = async () => {
    if (!userChatId) {
      showAlert('error', 'Please set your Telegram Chat ID first');
      return;
    }

    if (apiStatus !== 'connected') {
      showAlert('error', 'API server is not available. Please try again later.');
      return;
    }

    const currentPrice = livePricesRef.current[symbol]?.value;
    if (!price || isNaN(price)) {
      showAlert('error', 'Please enter a valid price');
      return;
    }

    const target = parseFloat(price);
    const direction = currentPrice > target ? 'down' : 'up';

    try {
      const response = await api.post('/alerts', {
        chat_id: userChatId,
        symbol,
        target,
        direction,
        triggered: false,
      });
      
      showAlert('success', 'Alert saved successfully!', {
        body: `Alert set for ${symbol} at ${price}`
      });
      fetchAlerts();
      setPrice(''); // Clear the input field
    } catch (error) {
      console.error('Error saving alert:', error);
      if (error.response?.data?.message) {
        showAlert('error', error.response.data.message);
      } else if (error.code === 'ERR_NETWORK') {
        showAlert('error', 'Network error: Cannot connect to server. Please check your internet connection.');
      } else if (error.response?.status === 404) {
        showAlert('error', 'API endpoint not found. Please contact administrator.');
      } else {
        showAlert('error', 'Failed to save alert. Please try again.');
      }
    }
  };

  // UPDATED: Mark alert as triggered using Laravel API
  const markAlertAsTriggered = async (id) => {
    try {
      await api.patch(`/alerts/${id}/triggered`);
    } catch (err) {
      console.error(`Failed to mark alert ${id} as triggered`, err);
    }
  };

  // UPDATED: Fetch alerts from Laravel API with better error handling
  const fetchAlerts = async (retries = 3) => {
    if (!userChatId || apiStatus !== 'connected') return;

    try {
      const response = await api.get(`/alerts?chat_id=${userChatId}`);
      setAlerts(response.data);
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
      if (retries > 0) {
        setTimeout(() => fetchAlerts(retries - 1), 2000);
      } else {
        if (err.response?.status === 404) {
          showAlert('error', 'Alerts endpoint not found. The API may not be properly configured.');
        }
      }
    }
  };

  // UPDATED: Delete alert using Laravel API
  const deleteAlert = async (id) => {
    if (!userChatId) return;

    try {
      await api.delete(`/alerts/${id}?chat_id=${userChatId}`);
      fetchAlerts();
      showAlert('success', 'Alert deleted successfully!');
    } catch (err) {
      console.error(`Failed to delete alert ${id}`, err);
      showAlert('error', 'Failed to delete alert. Please try again.');
    }
  };

  // Alert triggering logic
  useEffect(() => {
    const checkAlerts = async () => {
      const updates = [];
      
      alerts.forEach(alert => {
        if (processedAlerts.has(alert.id)) return;

        const currentPrice = livePricesRef.current[alert.symbol]?.value;
        if (currentPrice === null || currentPrice === undefined) return;

        const shouldTrigger = (alert.direction === 'up' && currentPrice >= alert.target) ||
                           (alert.direction === 'down' && currentPrice <= alert.target);

        if (shouldTrigger) {
          console.log(`ALERT TRIGGERED: ${alert.symbol} at ${currentPrice}`);
          updates.push({ alert, currentPrice });
        }
      });

      if (updates.length > 0) {
        await Promise.all(
          updates.map(async ({ alert, currentPrice }) => {
            const message = `🔔 Alert: ${alert.symbol} reached ${alert.target} (Current: ${currentPrice.toFixed(5)})`;
            
            // The Telegram message is now sent automatically by the Laravel backend
            // when the alert is triggered via the scheduler
            
            showAlert('warning', message.replace('🔔 ', ''), {
              body: `${alert.symbol} hit target price ${alert.target}`,
              symbol: alert.symbol
            });
            
            await markAlertAsTriggered(alert.id);
            setProcessedAlerts(prev => new Set([...prev, alert.id]));
          })
        );
        fetchAlerts();
      }
    };

    checkAlerts();
  }, [alerts, processedAlerts]);

  // Initialize and poll alerts
  useEffect(() => {
    if (userChatId && apiStatus === 'connected') {
      fetchAlerts();
      const interval = setInterval(fetchAlerts, 60000);
      return () => clearInterval(interval);
    }
  }, [userChatId, apiStatus]);

  // Update refs
  useEffect(() => {
    livePricesRef.current = symbolLivePrices;
  }, [symbolLivePrices]);

  useEffect(() => {
    setMonitoredSymbols(new Set(alerts.map(alert => alert.symbol)));
  }, [alerts]);

  return (
    <Box sx={{ 
      p: { xs: 2, sm: 4 }, 
      minHeight: '100vh',
      width: '100%',
      maxWidth: 1200,
      mx: 'auto',
      backgroundColor: tradingViewColors.background,
    }}>
      <Paper sx={{ 
        width: '100%',
        p: { xs: 2, sm: 4 },
        boxShadow: 3,
        overflow: 'hidden',
        backgroundColor: tradingViewColors.background,
        color: tradingViewColors.text,
      }}>
        <Typography variant="h5" gutterBottom sx={{ color: tradingViewColors.text }}>
          Deriv Price Alert System
        </Typography>
        
        {/* API Status Indicator */}
        <Box sx={{ 
          mb: 2, 
          p: 2, 
          backgroundColor: apiStatus === 'connected' ? tradingViewColors.green : tradingViewColors.red,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="body2" sx={{ color: tradingViewColors.text }}>
            API Status: {apiStatus === 'connected' ? 'Connected' : apiStatus === 'error' ? 'Disconnected' : 'Checking...'}
          </Typography>
          <Button 
            variant="outlined" 
            size="small"
            onClick={() => {
              setApiStatus('checking');
              setTimeout(() => {
                const checkApiHealth = async () => {
                  try {
                    const response = await api.get('/health');
                    if (response.data.status === 'ok') {
                      setApiStatus('connected');
                    } else {
                      setApiStatus('error');
                    }
                  } catch (error) {
                    setApiStatus('error');
                  }
                };
                checkApiHealth();
              }, 1000);
            }}
            sx={{ color: tradingViewColors.text, borderColor: tradingViewColors.text }}
          >
            Retry Connection
          </Button>
        </Box>

        {!userChatId && (
          <Box sx={{ 
            mb: 2, 
            p: 2, 
            backgroundColor: tradingViewColors.grid,
            borderRadius: 1,
          }}>
            <Typography variant="body2" sx={{ color: tradingViewColors.text }}>
              ⚠️ Please set your Telegram Chat ID to use alerts. Message your bot on Telegram and enter the chat ID when prompted.
            </Typography>
          </Box>
        )}

        {userChatId && (
          <Box sx={{ 
            mb: 2, 
            p: 1, 
            backgroundColor: tradingViewColors.grid,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Typography variant="body2" sx={{ color: tradingViewColors.text }}>
              User Chat ID: {userChatId}
            </Typography>
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => {
                localStorage.removeItem('telegram_chat_id');
                setUserChatId(null);
              }}
              sx={{ color: tradingViewColors.text }}
            >
              Change Chat ID
            </Button>
          </Box>
        )}
        
        {notificationPermission === 'default' && (
          <Box sx={{ 
            mb: 2, 
            p: 2, 
            backgroundColor: tradingViewColors.grid,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Typography variant="body2">
              Enable notifications to receive alerts even when the app is closed
            </Typography>
            <Button 
              variant="contained" 
              size="small"
              onClick={() => {
                Notification.requestPermission().then(permission => {
                  setNotificationPermission(permission);
                  if (permission === 'granted') {
                    showAlert('success', 'Notifications enabled!');
                  }
                });
              }}
            >
              Enable Notifications
            </Button>
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: connectionStatus === 'connected' ? tradingViewColors.green : tradingViewColors.red
            }} />
            <Typography variant="caption" sx={{ color: tradingViewColors.text }}>
              {connectionStatus === 'connected' ? 'Live' : 'Disconnected'}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: tradingViewColors.text }}>
            Monitoring: {monitoredSymbols.size > 0 
              ? Array.from(monitoredSymbols).join(', ') 
              : 'No active alerts'}
          </Typography>
        </Box>

        <AppBar position="static" color="default" sx={{ overflowX: 'auto', backgroundColor: tradingViewColors.background }}>
          <Tabs
            value={category}
            onChange={(e, newVal) => {
              setCategory(newVal);
              setSymbol(SYMBOL_CATEGORIES[newVal][0]);
              setSymbolLivePrices(prev => ({
                ...prev,
                [SYMBOL_CATEGORIES[newVal][0]]: {
                  ...prev[SYMBOL_CATEGORIES[newVal][0]],
                  loading: true
                }
              }));
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minWidth: 'fit-content',
              '& .MuiTabs-indicator': {
                backgroundColor: tradingViewColors.green,
              },
            }}
          >
            {Object.keys(SYMBOL_CATEGORIES).map(cat => (
              <Tab 
                key={cat} 
                label={cat} 
                value={cat} 
                sx={{ 
                  px: { xs: 1, sm: 2 },
                  minWidth: 'unset',
                  color: tradingViewColors.text,
                  '&.Mui-selected': {
                    color: tradingViewColors.green,
                  },
                }}
              />
            ))}
          </Tabs>
        </AppBar>

        <Box mt={2}>
          <TextField
            select
            fullWidth
            label="Select Pair"
            value={symbol}
            onChange={(e) => {
              setSymbol(e.target.value);
              setSymbolLivePrices(prev => ({
                ...prev,
                [e.target.value]: {
                  ...prev[e.target.value],
                  loading: true
                }
              }));
            }}
            sx={{ 
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: tradingViewColors.grid,
                },
                '&:hover fieldset': {
                  borderColor: tradingViewColors.grid,
                },
                '&.Mui-focused fieldset': {
                  borderColor: tradingViewColors.green,
                },
              },
              '& .MuiInputLabel-root': {
                color: tradingViewColors.text,
              },
              '& .MuiSelect-select': {
                color: tradingViewColors.text,
              },
            }}
          >
            {SYMBOL_CATEGORIES[category].map(sym => (
              <MenuItem 
                key={sym} 
                value={sym}
                sx={{
                  backgroundColor: tradingViewColors.background,
                  color: tradingViewColors.text,
                  '&:hover': {
                    backgroundColor: tradingViewColors.grid,
                  },
                }}
              >
                {sym}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              fullWidth
              type="number"
              inputProps={{ min: 0.00001, step: "0.00001" }}
              label="Alert Price"
              value={price}
              onChange={(e) => e.target.value >= 0 && setPrice(e.target.value)}
              sx={{ 
                width: { xs: '100%', sm: 'auto' },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: tradingViewColors.grid,
                  },
                  '&:hover fieldset': {
                    borderColor: tradingViewColors.grid,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: tradingViewColors.green,
                  },
                },
                '& .MuiInputLabel-root': {
                  color: tradingViewColors.text,
                },
                '& .MuiInputBase-input': {
                  color: tradingViewColors.text,
                },
              }}
            />
            <Button 
              onClick={() => fetchRealFallbackPrices(new Set([symbol]))}
              startIcon={<RefreshIcon />}
              sx={{ 
                minWidth: 120,
                width: { xs: '100%', sm: 'auto' },
                mt: { xs: 2, sm: 0 },
                color: tradingViewColors.text,
                borderColor: tradingViewColors.grid,
                '&:hover': {
                  borderColor: tradingViewColors.green,
                },
              }}
              variant="outlined"
            >
              Refresh
            </Button>
          </Box>

          <Button 
            fullWidth 
            variant="contained" 
            onClick={handleSubmit} 
            disabled={!userChatId || apiStatus !== 'connected'}
            sx={{ 
              mt: 2,
              backgroundColor: tradingViewColors.green,
              '&:hover': {
                backgroundColor: '#1d8a7e',
              },
              '&:disabled': {
                backgroundColor: tradingViewColors.grid,
                color: tradingViewColors.text,
              },
            }}
          >
            {!userChatId ? 'Set Chat ID First' : apiStatus !== 'connected' ? 'API Not Connected' : 'Set Alert'}
          </Button>
        </Box>

        <Box mt={3}>
          <PriceDisplay 
            priceState={symbolLivePrices[symbol] || PRICE_STATE_TEMPLATE} 
            onRefresh={() => fetchRealFallbackPrices(new Set([symbol]))}
          />
        </Box>

        <Box mt={4} id="chart-container">
          <Typography variant="h6" gutterBottom sx={{ color: tradingViewColors.text }}>
            Candlestick Chart
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel sx={{ color: tradingViewColors.text }}>Timeframe</InputLabel>
            <Select
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value)}
              label="Timeframe"
              sx={{
                color: tradingViewColors.text,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: tradingViewColors.grid,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: tradingViewColors.grid,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: tradingViewColors.green,
                },
              }}
            >
              {TIMEFRAME_OPTIONS.map((option) => (
                <MenuItem 
                  key={option.value} 
                  value={option.value}
                  sx={{
                    backgroundColor: tradingViewColors.background,
                    color: tradingViewColors.text,
                    '&:hover': {
                      backgroundColor: tradingViewColors.grid,
                    },
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <CandleChart 
            data={ohlcData} 
            loading={chartLoading} 
            width={width - 32}
            currentPrice={symbolLivePrices[symbol]?.value}
          />
        </Box>

        {/* Alerts List Section */}
        {userChatId && alerts.length > 0 && (
          <Box mt={4}>
            <Typography variant="h6" gutterBottom sx={{ color: tradingViewColors.text }}>
              Your Active Alerts ({alerts.length})
            </Typography>
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              {alerts.map((alert) => (
                <Paper 
                  key={alert.id} 
                  sx={{ 
                    p: 2, 
                    mb: 1, 
                    backgroundColor: tradingViewColors.grid,
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center'
                  }}
                >
                  <Box>
                    <Typography sx={{ color: tradingViewColors.text, fontWeight: 'bold' }}>
                      {alert.symbol}
                    </Typography>
                    <Typography variant="body2" sx={{ color: tradingViewColors.text }}>
                      Target: {alert.target} | Direction: {alert.direction}
                    </Typography>
                  </Box>
                  <Button 
                    variant="outlined" 
                    color="error" 
                    size="small"
                    onClick={() => deleteAlert(alert.id)}
                    sx={{ color: tradingViewColors.red, borderColor: tradingViewColors.red }}
                  >
                    Delete
                  </Button>
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        <AlertPopup 
          open={alertOpen}
          onClose={() => setAlertOpen(false)}
          severity={alertSeverity}
          message={alertMessage}
        />
      </Paper>
    </Box>
  );
}
